import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchActiveHappyHour, type HappyHour } from '../services/happyHourApi';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface UseHappyHourOptions {
  /** Désactive le polling (ex: mode admin) */
  enabled?: boolean;
}

interface UseHappyHourResult {
  /** Happy hour actif, ou null */
  happyHour: HappyHour | null;
  /** Force un refresh (ex: retour à l'écran d'accueil = nouveau client) */
  refresh: () => Promise<void>;
}

/**
 * v5 — Hook Happy Hour.
 *
 * Appelle get_active_happy_hour :
 *  - au démarrage
 *  - toutes les 5 minutes
 *  - à la demande via refresh() (retour accueil / nouveau client)
 *
 * Sécurité : en cas d'erreur réseau → happyHour = null (aucune remise inventée).
 */
export function useHappyHour(options: UseHappyHourOptions = {}): UseHappyHourResult {
  const { enabled = true } = options;
  const [happyHour, setHappyHour] = useState<HappyHour | null>(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const result = await fetchActiveHappyHour();
    if (!mountedRef.current) return;
    setHappyHour(result);
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      setHappyHour(null);
      return;
    }

    refresh();
    const intervalId = setInterval(refresh, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [enabled, refresh]);

  return { happyHour, refresh };
}
